import React, { useMemo } from 'react';
import classes from './PostCardMediaPreviewFacebook.module.scss';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import { SocialProfileWithBoards } from '../../../../hooks/useCategoriesProfilesWithBoards';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import { serializeText, serializeTextToHtml } from '../../TextEditor/helpers/serializer';
import classNames from 'classnames';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import {
  GenericProfileType,
  MediaProportions,
  MediaType,
  Network,
  PostFeedType,
  PostMultiMediaAttachMode,
  SocialOAuthProvider
} from '../../../../models/graphql';
import { FacebookLinkPreviewCard } from '../FacebookLinkPreviewCard';
import { Loader } from '../../../../components/containers/Loader';
import { FirstCommentPreview } from '../FirstCommentPreview';
import { canAddComment } from '../../helpers/canAddComment';
import { SingularValidator } from '../../validators/helpers/singular-validator';
import { InvalidMediaSet } from '../PostCardMediaPreview/InvalidMediaSet';
import { MediaTypePostEditorState } from '../../../../models/post-editor';
import { Badge } from '../../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../../constants/badge';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';

export type PostCardMediaPreviewFacebookProfile = {
  socialProfile: SocialProfileWithBoards & { __typename: 'FacebookSocialProfile' };
};

export type PostCardMediaPreviewFacebookViewProps = PostCardPreviewMediaDataType & PostCardMediaPreviewFacebookProfile;

export type PostPreviewFacebookLayoutType =
  | 'One'
  | 'TwoWide'
  | 'TwoTall'
  | 'TwoSquare'
  | 'ThreeWide'
  | 'ThreeTall'
  | 'ThreeWideSquare'
  | 'ThreeTallSquare'
  | 'Four'
  | 'Five';

const getLayoutType = (types: Array<'T' | 'W' | 'S'>): PostPreviewFacebookLayoutType => {
  const result = types.join('');
  switch (types.length) {
    case 1:
      return 'One';
    case 2:
      if (result === 'TT') return 'TwoTall';
      else if (result === 'WW') return 'TwoWide';
      return 'TwoSquare';
    case 3:
      if (['TTW', 'TTS', 'TWS', 'TSW', 'TSS'].includes(result)) return 'ThreeTallSquare';
      else if (['WTS', 'WST', 'WSS', 'SSS'].includes(result)) return 'ThreeWideSquare';
      else if (['TTT', 'TWT', 'TST', 'WTT', 'STT', 'STW', 'STS', 'SWT', 'SST'].includes(result)) return 'ThreeTall';
      else return 'ThreeWide';
    case 4:
      return 'Four';
    default:
      return 'Five';
  }
};

export const isInvalidFacebookMedia = (media: MediaTypePostEditorState[]): boolean =>
  SingularValidator.noMixedMediaInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
  SingularValidator.facebookMediaLimitInvalid(media, PostMultiMediaAttachMode.MULTIPLE);

export const PostCardMediaPreviewFacebookView = (props: PostCardMediaPreviewFacebookViewProps): JSX.Element => {
  const { text, media, socialProfile, firstComment, link } = props;
  const loadingLinkMetadata = link && link.linkMetadata === undefined;
  const linkMetadata = link?.linkMetadata;
  const serializedText = serializeText(text);
  const hasText = !!serializedText;

  const getMedia = useMemo(() => {
    if (!media.length) return <></>;
    if (isInvalidFacebookMedia(media))
      return (
        <div className={classNames(classes.MediaWrapper, hasText ? classes.MediaWrapperHasText : null)}>
          <InvalidMediaSet className={classNames(classes.MediaItem, classes.MediaInvalid)} />
        </div>
      );
    const baseMediaClasses = media.length > 1 ? classes.MediaItem : classes.MediaItemSolo;
    let computedMediaItemClasses = '';
    const mediaTypes = media.map(item => {
      if (item.proportions === MediaProportions.TALL) return 'T';
      else if (item.proportions === MediaProportions.WIDE) return 'W';
      return 'S';
    });
    const layoutType = getLayoutType(mediaTypes);
    const getClasses = (index: number, mediaLength: number): string => {
      switch (mediaLength) {
        case 1:
          computedMediaItemClasses = classNames(baseMediaClasses);
          break;
        case 2:
          if (layoutType === 'TwoTall') {
            computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaTallHalf, {
              [classes.TopLeft]: index === 0,
              [classes.TopRight]: index === 1
            });
          } else if (layoutType === 'TwoWide') {
            computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaWideHalf, {
              [classes.TopLeft]: index === 0,
              [classes.BottomLeft]: index === 1
            });
          } else {
            computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaSquareHalf, {
              [classes.TopLeft]: index === 0,
              [classes.TopRight]: index === 1
            });
          }
          break;
        case 3:
          if (layoutType === 'ThreeWide')
            computedMediaItemClasses = classNames(
              baseMediaClasses,
              index === 0 ? classes.MediaWideTwoThird : classes.MediaWideThird,
              {
                [classes.TopLeft]: index === 0,
                [classes.BottomLeft]: index === 1,
                [classes.BottomRight]: index === 2
              }
            );
          else if (layoutType === 'ThreeTall')
            computedMediaItemClasses = classNames(
              baseMediaClasses,
              index === 0 ? classes.MediaTallTwoThird : classes.MediaTallThird,
              {
                [classes.TopLeft]: index === 0,
                [classes.TopRight]: index === 1,
                [classes.BottomRight]: index === 2
              }
            );
          else if (layoutType === 'ThreeTallSquare')
            computedMediaItemClasses = classNames(
              baseMediaClasses,
              index === 0 ? classes.MediaTallHalf : classes.MediaSquareFourth,
              {
                [classes.TopLeft]: index === 0,
                [classes.TopRight]: index === 1,
                [classes.BottomRight]: index === 2
              }
            );
          else {
            computedMediaItemClasses = classNames(
              baseMediaClasses,
              index === 0 ? classes.MediaWideHalf : classes.MediaSquareFourth,
              {
                [classes.TopLeft]: index === 0,
                [classes.BottomLeft]: index === 1,
                [classes.BottomRight]: index === 2
              }
            );
          }
          break;
        case 4:
          computedMediaItemClasses = classNames(baseMediaClasses, classes.MediaSquareFourth, {
            [classes.TopLeft]: index === 0,
            [classes.TopRight]: index === 1,
            [classes.BottomLeft]: index === 2,
            [classes.BottomRight]: index === 3
          });
          break;
        default:
          computedMediaItemClasses = classNames(
            baseMediaClasses,
            index < 2 ? classes.MediaFiveTop : classes.MediaFiveBottom,
            {
              [classes.TopLeft]: index === 0,
              [classes.TopRight]: index === 1,
              [classes.BottomLeft]: index === 2,
              [classes.BottomCentre]: index === 3,
              [classes.BottomRight]: index === 4
            }
          );
          break;
      }
      return computedMediaItemClasses;
    };
    return (
      <>
        <div
          className={classNames(classes.MediaWrapper, hasText ? classes.MediaWrapperHasText : null, {
            [classes.MediaWrapperHalfHeight]: layoutType === 'TwoSquare',
            [classes.MediaWrapperSquare]: !['One', 'TwoSquare'].includes(layoutType) && media.length < 5,
            [classes.MediaWrapperFiveImages]: media.length > 4
          })}>
          {media
            .filter((_, index) => index < 5)
            .map((mediaItem, index) => {
              return (
                <div className={getClasses(index, media.length)} key={`media-item-${index}`}>
                  <img
                    src={mediaItem.type === MediaType.VIDEO ? mediaItem.thumbnailUrl : mediaItem.url}
                    alt={`facebook preview ${index + 1}`}
                  />
                  {index === 4 && media.length > 5 ? (
                    <div className={classes.MoreThanFiveOverlay}>
                      <h2 className={classes.MoreThanFiveText}>+{media.length - 5}</h2>
                    </div>
                  ) : null}
                  {media[index].type === MediaType.VIDEO ? (
                    <div className={classes.MediaMarker}>
                      <Icon name={IconsEnum.play} size={'large'} />
                    </div>
                  ) : null}
                  {media[index].gif ? (
                    <div className={classes.GifMarker}>
                      <Icon name={IconsEnum.gifBadge} size={'large'} />
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      </>
    );
  }, [media, hasText]);

  const network = profilesHelpers.getNetwork(socialProfile);
  const genericProfile = profilesHelpers.getGenericProfile(socialProfile);
  const isFacebookGeneric = network === Network.GENERIC_PROFILE;

  const sublabelText = isFacebookGeneric
    ? genericProfile === GenericProfileType.FACEBOOK_PERSONAL
      ? 'Facebook Personal Profile - publish via notification'
      : genericProfile === GenericProfileType.FACEBOOK_GROUP
      ? 'Facebook Group - publish via notification'
      : socialProfile.type === 'page'
      ? 'Facebook Page'
      : socialProfile.type === 'group'
      ? 'Facebook Group'
      : 'Facebook Profile'
    : '';

  return (
    <>
      <div className={classes.SocialProfileWrapper}>
        <div className={'grid-x align-justify align-middle'}>
          <div className={classNames('cell auto', isFacebookGeneric ? classes.SocialProfileWrapperMaxWidth : null)}>
            <SocialProfile
              socialProfile={{
                id: socialProfile.id,
                provider: SocialOAuthProvider.FACEBOOK,
                network: Network.FACEBOOK,
                name: socialProfile.displayName,
                type: socialProfile.type
              }}
              disconnected={!socialProfile.connected}
              sublabel={true}
              sublabelText={sublabelText}
              size={'medium'}
              label={true}
              image={socialProfile.photoUrl}
            />
          </div>
          {isFacebookGeneric ? (
            <div className={classNames('cell shrink', classes.ReminderNotificationWrapper)}>
              <Badge
                badgeKey={`notification-${socialProfile.id}-${socialProfile.__typename}`}
                size={BadgeSizesEnum.LARGE}
                icon={IconsEnum.bell}
                side={'left'}
                color={BadgeColorsEnum.GREY20}
                tooltipText={"You'll receive a notification when this post is about to get published"}
                text={'Notification'}
              />
            </div>
          ) : null}
        </div>
      </div>
      {serializedText ? (
        <div
          className={classes.TextWrapper}
          dangerouslySetInnerHTML={{ __html: serializeTextToHtml(text, SocialNetworksEnum.facebook) }}></div>
      ) : null}
      {media.length ? getMedia : null}
      {(loadingLinkMetadata || linkMetadata) && link && !media.length ? (
        <div className={classNames(classes.LinkPreviewWrapper, classes.LinkPreviewWrapperLarge)}>
          {/* ^^ TODO hardcoded since we still don't have image size from the link preview */}
          {loadingLinkMetadata ? <Loader variation={'dark'} size={'small'} type={'inline'} /> : null}
          {linkMetadata && !link.removePreview ? (
            <FacebookLinkPreviewCard
              link={link.url}
              size={'large'} // TODO hardcoded since we still don't have image size from the link preview
              description={linkMetadata.openGraph?.description || ''}
              title={linkMetadata.openGraph?.title || ''}
              image={linkMetadata.openGraph?.image || ''}
              __typename={'OpenGraph'}
            />
          ) : null}
        </div>
      ) : null}
      <div className={classNames(classes.FooterWrapper, 'grid-x', 'align-justify')}>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.thumbUp} size={'medium'} />
            <div className={classes.FooterItemText}>Like</div>
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.comments} size={'medium'} />
            <div className={classes.FooterItemText}>Comment</div>
          </div>
        </div>
        <div className={'cell shrink'}>
          <div className={classNames('grid-x', 'align-center', 'align-middle', classes.FooterItem)}>
            <Icon name={IconsEnum.share} size={'medium'} />
            <div className={classes.FooterItemText}>Share</div>
          </div>
        </div>
      </div>
      {firstComment && canAddComment([socialProfile], PostFeedType.FEED) ? (
        <FirstCommentPreview firstComment={firstComment} socialProfile={socialProfile} />
      ) : null}
    </>
  );
};
